import { useReducer, useRef, useEffect } from 'react';
import { tabbable } from 'tabbable';

const defaultState = {
    open: false,
};
function useModalState(initialState = defaultState) {
    function reducer(state, action) {
        if (action.type === 'open') {
            return {
                ...state,
                open: true,
            };
        }
        if (action.type === 'close') {
            return {
                ...state,
                open: false,
            };
        }
        return state;
    }
    return useReducer(reducer, initialState);
}
function useFocusTrap() {
    const trapRef = useRef(null);
    useEffect(() => {
        function trapHandler({ target: elementReceivingFocus, relatedTarget: elementLosingFocus, }) {
            const isFocusableElement = (target) => target instanceof HTMLElement || target instanceof SVGElement;
            const isOutsideTrap = (target) => {
                var _a;
                return !(isFocusableElement(target) &&
                    ((_a = trapRef.current) === null || _a === void 0 ? void 0 : _a.contains(target)));
            };
            if (trapRef.current) {
                const tabbableElements = tabbable(trapRef.current);
                if (tabbableElements.length > 0 &&
                    isOutsideTrap(elementReceivingFocus)) {
                    const [firstTabbableElement] = tabbableElements;
                    const [lastTabbableElement] = tabbableElements.slice(-1);
                    const newFocusTarget = isFocusableElement(elementLosingFocus) &&
                        tabbableElements.indexOf(elementLosingFocus) === 0
                        ? lastTabbableElement
                        : firstTabbableElement;
                    window.requestAnimationFrame(() => {
                        newFocusTarget.focus();
                    });
                }
            }
        }
        document.addEventListener('focus', trapHandler, true);
        return () => {
            document.removeEventListener('focus', trapHandler, true);
        };
    }, []);
    return trapRef;
}
/**
 * Simple scroll lock implementation adapted from prisma.fi and react-aria.
 *
 * @param isOn `boolean` lock state
 * @see https://gitlab.sok.fi/sok-digikehitys/prismafi/prismafi-website/-/blob/main/frontend/src/utils/hooks/common.ts#L187
 * @see https://github.com/adobe/react-spectrum/blob/main/packages/%40react-aria/overlays/src/usePreventScroll.ts
 */
function useScrollLock(isOn = true) {
    useEffect(() => {
        if (isOn) {
            const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
            // Styles are intentionally set to the documentElement aka <html>
            // to reduce the likelyhood to conflict with e.g. <body< paddings.
            document.documentElement.style.overflow = 'hidden';
            document.documentElement.style.paddingRight = `${scrollbarWidth}px`;
            document.documentElement.style.touchAction = 'none';
        }
        return () => {
            document.documentElement.style.overflow = '';
            document.documentElement.style.paddingRight = '';
            document.documentElement.style.touchAction = '';
        };
    }, [isOn]);
}
/**
 * Prevents screen reader users to navigate to elements that are behind
 * the modal blanket while the modal dialog is open.
 *
 * The hook sets the sibling elements of the modal blanket to `inert` state,
 * which removes them from the accessibility tree. Additionally, `aria-hidden`
 * attribute is set for backwards compatibility.
 *
 * @param isOn `boolean` modal state
 * @returns A reference to the modal blanket
 * @see https://developer.mozilla.org/en-US/docs/Web/HTML/Global_attributes/inert
 */
function useInert(isOn = true) {
    const blanketRef = useRef(null);
    useEffect(() => {
        var _a, _b, _c;
        const nonRenderedTags = ['SCRIPT', 'STYLE', 'TEMPLATE'];
        const siblings = Array.from((_c = (_b = (_a = blanketRef.current) === null || _a === void 0 ? void 0 : _a.parentElement) === null || _b === void 0 ? void 0 : _b.children) !== null && _c !== void 0 ? _c : []).filter((element) => element !== blanketRef.current &&
            !nonRenderedTags.includes(element.tagName) &&
            !element.hasAttribute('aria-hidden') &&
            !element.hasAttribute('inert'));
        if (isOn && blanketRef.current) {
            for (const element of siblings) {
                element.setAttribute('aria-hidden', 'true');
                element.setAttribute('inert', '');
            }
        }
        return () => {
            for (const element of siblings) {
                element.removeAttribute('aria-hidden');
                element.removeAttribute('inert');
            }
        };
    }, [blanketRef, isOn]);
    return blanketRef;
}

export { useFocusTrap, useInert, useModalState, useScrollLock };
//# sourceMappingURL=hooks.js.map
